import React, { useState, useEffect } from "react";
import Layout from "../components/Layout/Layout";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getImageUrl, FALLBACK_IMAGE } from "../utils/imageHelper";
import api from "../utils/axiosInstance";
import { FiTrash2 } from "react-icons/fi";
import SortFilter from "../components/ProductFilter";

const Wishlist = () => {
  const navigate = useNavigate();
  const [wishlist, setWishlist] = useState([]);
  const [filteredWishlist, setFilteredWishlist] = useState([]);
  const [loading, setLoading] = useState(false);

  const PALETTE = {
    PRIMARY_BLUE: "#6A9DD5",
    DARK_NAVY: "#1C2E4A",
    ACCENT_BROWN: "#7C4F2B",
  };

  useEffect(() => {
    getWishlistItems();
  }, []);

  const getWishlistItems = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/api/v1/wishlist");
      const wishlistItems = data?.wishlist || [];

      setWishlist(wishlistItems);
      setFilteredWishlist(wishlistItems);
    } catch (error) {
      console.error("Error fetching wishlist:", error);
      toast.error("Failed to load wishlist");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (productId) => {
    try {
      const { data } = await api.delete(`/api/v1/wishlist/${productId}`);
      if (data?.success) {
        toast.success("Removed from wishlist");
        getWishlistItems();
      }
    } catch (error) {
      console.error("Error removing item:", error);
      toast.error("Failed to remove item");
    }
  };

  const handleSort = (sortValue) => {
    let sorted = [...filteredWishlist];

    switch (sortValue) {
      case "priceLowHigh":
        sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;
      case "priceHighLow":
        sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;
      case "newest":
        sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case "featured":
      default:
        sorted = [...wishlist];
        break;
    }

    setFilteredWishlist(sorted);
  };

  const handleFilter = (filters) => {
    let filtered = [...wishlist];

    if (filters.categories.length > 0) {
      filtered = filtered.filter((product) => {
        const categoryName =
          product.category?.name ||
          product.category?.slug ||
          product.category ||
          product.categoryName;

        return filters.categories.includes(categoryName);
      });
    }

    filtered = filtered.filter((product) => {
      const productPrice = product.price || 0;
      return productPrice <= filters.price;
    });

    setFilteredWishlist(filtered);
  };

  return (
    <Layout>
      <section className="w-full mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2
              className="text-2xl sm:text-3xl font-light tracking-widest uppercase"
              style={{ color: PALETTE.DARK_NAVY }}
            >
              Your Wishlist
            </h2>
            <h4 className="mt-2 sm:mt-3 text-sm sm:text-base text-gray-600 font-light">
              {filteredWishlist?.length} item(s)
              {filteredWishlist.length !== wishlist.length &&
                ` (filtered from ${wishlist.length} total)`}
            </h4>
            <div
              className="w-16 sm:w-24 h-px mx-auto mt-4"
              style={{ backgroundColor: PALETTE.ACCENT_BROWN }}
            />
          </div>

          <SortFilter onSort={handleSort} onFilter={handleFilter} />

          <div className="flex-1 mt-6 sm:mt-8">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-pulse flex flex-col items-center">
                  <div className="h-4 w-4 bg-gray-300 rounded-full mb-2"></div>
                  <span className="text-gray-400">Loading wishlist...</span>
                </div>
              </div>
            ) : filteredWishlist.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-gray-500 text-lg mb-6 font-light">
                  {wishlist.length === 0
                    ? "Your wishlist is currently empty."
                    : "No items match your filters."}
                </p>
                {wishlist.length > 0 && (
                  <button
                    onClick={() => {
                      setFilteredWishlist(wishlist);
                      toast.success("Filters cleared");
                    }}
                    className="px-6 py-2 sm:px-8 sm:py-3 mb-4 bg-gray-600 text-white text-xs sm:text-sm tracking-widest uppercase hover:bg-gray-700 transition-colors mr-4"
                  >
                    Clear Filters
                  </button>
                )}
                <button
                  onClick={() => navigate("/")}
                  className="px-6 py-2 sm:px-8 sm:py-3 bg-black text-white text-xs sm:text-sm tracking-widest uppercase hover:bg-gray-800 transition-colors"
                >
                  Continue Shopping
                </button>
              </div>
            ) : (
              // Updated Grid: grid-cols-2 on mobile (base), gap reduced for mobile
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-3 sm:gap-x-8 gap-y-6 sm:gap-y-12">
                {filteredWishlist.map((product) => (
                  <div
                    key={product._id}
                    className="group w-full bg-white overflow-hidden transition-all duration-300 border border-gray-100 sm:border-none"
                  >
                    {/* Image Container: Reduced height for mobile */}
                    <div className="relative w-full h-48 sm:h-96 overflow-hidden bg-gray-50">
                      <div
                        className="w-full h-full cursor-pointer"
                        onClick={() => navigate(`/product/${product.slug}`)}
                      >
                        <img
                          src={getImageUrl(product.photos?.[0])}
                          onError={(e) => (e.target.src = FALLBACK_IMAGE)}
                          alt={product.name}
                          className="w-full h-full object-cover object-top transition-transform duration-700 ease-in-out group-hover:scale-105"
                        />
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemove(product._id);
                        }}
                        className="absolute top-2 right-2 sm:top-4 sm:right-4 p-1.5 sm:p-2 bg-white/90 backdrop-blur-sm rounded-full text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors shadow-sm z-10"
                        title="Remove from wishlist"
                      >
                        <FiTrash2 className="text-sm sm:text-lg" />
                      </button>

                      {product.stock <= 0 && (
                        <div className="absolute top-2 left-2 sm:top-4 sm:left-4 bg-red-500 text-white text-[10px] sm:text-xs px-2 py-0.5 sm:px-3 sm:py-1 uppercase tracking-wider font-bold">
                          Out of Stock
                        </div>
                      )}
                    </div>

                    {/* Details: Reduced padding and font sizes for mobile */}
                    <div className="p-3 sm:p-6">
                      <h3 className="text-sm sm:text-lg font-semibold text-gray-800 mb-1 sm:mb-2 tracking-wide leading-tight">
                        {product.name.length > 20
                          ? `${product.name.slice(0, 20)}...`
                          : product.name}
                      </h3>

                      <p className="text-xs sm:text-sm text-gray-500 mb-2 sm:mb-3 line-clamp-2 hidden sm:block">
                        {product.description?.length > 30
                          ? `${product.description.slice(0, 30)}...`
                          : product.description}
                      </p>

                      <div className="flex items-center justify-between mb-3 sm:mb-4">
                        <span
                          className="text-sm sm:text-xl font-bold"
                          style={{ color: PALETTE.DARK_NAVY }}
                        >
                          {product.price?.toLocaleString("en-IN", {
                            style: "currency",
                            currency: "INR",
                          })}
                        </span>
                      </div>

                      <div className="flex gap-3">
                        <button
                          className="flex-1 py-1.5 sm:py-2 px-2 sm:px-4 text-[10px] sm:text-sm tracking-wider uppercase font-bold text-black bg-transparent border-[1px] border-black transition-all duration-300 hover:bg-black hover:text-white whitespace-nowrap"
                          onClick={() => navigate(`/product/${product.slug}`)}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Wishlist;
